/**
 * SocialPostTemplatesPage.tsx
 *
 * The account-side Social Post Templates tab: the master account's shared
 * default posts, authored in the corporate dashboard, plus this account's own.
 *
 * These are TEMPLATES: prepared content — caption, media, cover — with no
 * platform, account or date. Nothing here publishes. Zernio owns real posts and
 * keeps no local row, so the only path to a published post is "Use template",
 * which opens the existing ZernioPostComposer seeded with the content. The
 * account picks its social accounts and timing there, and that is where the
 * content is checked against each platform's rules. The template itself is never
 * changed by being used, so it can be reused as often as needed.
 *
 * ── Folder view ──
 * Drive-style: the page opens on a grid of folder tiles, and clicking one opens
 * it. Folders are one level deep. The open folder lives in the URL (?folder=id)
 * so the browser's Back button returns to the tiles, the way it does in Drive.
 * Inside a folder, templates show as cards or rows, and can be dragged onto
 * another folder to move them.
 *
 * Ownership follows the photo and video libraries exactly. A shared template,
 * and a folder the corporate team created, belong to the master account and are
 * read-only here — no edit, no delete, no move — and the API returns 422 if that
 * is attempted anyway. Deleting one of this account's own folders deletes the
 * templates inside it too (soft-deleted on the server).
 */
import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Plus, Trash2, Pencil, Copy, Send, Play, FolderOpen, Folder, FolderPlus,
  MessageSquareText, ChevronDown, ChevronRight, Check, LayoutGrid, List,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { usePageContext } from '@/hooks/usePageContext';
import { ZernioPostComposer } from './ZernioPostComposer';
import { ZernioMediaListPicker } from './ZernioMediaListPicker';
import { ZernioCoverPicker, type CoverSelection } from './ZernioCoverPicker';
import type { PickedMedia } from './ZernioMediaPicker';
import { coverOf, firstVideoSrc, toPickedMediaList, toTemplateMediaList } from './socialTemplateMedia';
import { SocialMediaPreviewModal } from './SocialMediaPreview';
import {
  getSocialPostTemplates,
  getSocialPostFolders,
  createSocialPostTemplate,
  updateSocialPostTemplate,
  deleteSocialPostTemplate,
  moveSocialPostTemplates,
  createSocialPostFolder,
  renameSocialPostFolder,
  deleteSocialPostFolder,
  type LibraryFolder,
  type SocialPostTemplate,
} from '@/services/marketing.service';

type ScopeFilter = 'all' | 'mine' | 'shared';
type ViewMode = 'grid' | 'list';

/** dataTransfer type for a dragged template; the payload is a JSON id list. */
const DRAG_TYPE = 'application/x-social-template-ids';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 0 || !Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function folderLabel(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** This account's own folder, the only kind it can rename, delete or empty. */
function canManageFolder(f: LibraryFolder): boolean {
  return f.scope === 'account' && !f.isSystem && f.id !== null;
}

function fromCorporate(t: SocialPostTemplate): boolean {
  return t.shared || t.sourcePostID != null;
}

/** Created by the corporate team for the whole group — read-only here. */
function isCorporateFolder(f: LibraryFolder): boolean {
  return f.scope === 'master' && !f.isSystem;
}

function apiMessage(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;
}

export default function SocialPostTemplatesPage({ embedded = false }: { embedded?: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const updateAIContext = usePageContext('Social Post Templates');
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  // Always opens as cards — deliberately not remembered between visits.
  const [view, setView] = useState<ViewMode>('grid');

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [moveDropdownOpen, setMoveDropdownOpen] = useState(false);
  // Set while a template is being dragged, which is what shows the drop targets.
  const [dragIds, setDragIds] = useState<number[] | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SocialPostTemplate | null>(null);

  // Folder dialogs: a name prompt for create/rename, a confirm for delete.
  const [nameDialog, setNameDialog] = useState<{ folder: LibraryFolder | null } | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<LibraryFolder | null>(null);

  // The composer is seeded from whichever template "Use template" was clicked on.
  const [composerOpen, setComposerOpen] = useState(false);
  const [seed, setSeed] = useState<{ content: string; media: PickedMedia[]; cover: CoverSelection | null }>({
    content: '',
    media: [],
    cover: null,
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['marketing', 'social-posts'],
    queryFn: () => getSocialPostTemplates(),
  });

  const { data: folders } = useQuery({
    queryKey: ['marketing', 'social-post-folders'],
    queryFn: () => getSocialPostFolders(),
  });

  const allFolders = useMemo(() => folders ?? [], [folders]);
  const generalId = allFolders.find((f) => f.isSystem && f.systemKey === 'general')?.id ?? null;

  /** A template's folder id. A legacy row with folderID 0 sits in General. */
  const folderOf = (t: SocialPostTemplate): number | null => (t.folderID > 0 ? t.folderID : generalId);

  const isSentByCorporate = (f: LibraryFolder): boolean => {
    if (f.scope !== 'account' || f.isSystem) return false;
    const items = (templates ?? []).filter((t) => folderOf(t) === f.id);
    return items.length > 0 && items.every(fromCorporate);
  };

  // ── The open folder, from the URL ─────────────────────────────────────────
  const requestedFolderId = Number(searchParams.get('folder')) || null;
  const openFolder = allFolders.find((f) => f.id === requestedFolderId) ?? null;

  const clearSelection = () => {
    setSelectedIds(new Set());
    setMoveDropdownOpen(false);
  };

  const goToFolder = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id === null) next.delete('folder');
    else next.set('folder', String(id));
    // A push, not a replace, so Back returns to the tiles.
    setSearchParams(next);
    clearSelection();
    setSearch('');
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();
  const matchesSearch = (t: SocialPostTemplate) =>
    !q || t.caption.toLowerCase().includes(q);

  const scoped = useMemo(() => {
    const all = templates ?? [];
    if (scopeFilter === 'shared') return all.filter(fromCorporate);
    if (scopeFilter === 'mine') return all.filter((t) => !fromCorporate(t));
    return all;
  }, [templates, scopeFilter]);

  const inFolder = (folderId: number | null) => scoped.filter((t) => folderOf(t) === folderId);

  /**
   * The tiles. With a search, only folders holding a match stay, each with its
   * match count. With the From Corporate / This Account filter, a folder with
   * nothing of that kind is hidden, so the filter reads the same on the tiles as
   * inside a folder.
   */
  const tiles = allFolders
    .map((f) => {
      const items = inFolder(f.id);
      return { folder: f, count: items.length, matches: items.filter(matchesSearch).length };
    })
    .filter((tile) => (q ? tile.matches > 0 : scopeFilter === 'all' || tile.count > 0));

  const folderItems = openFolder ? inFolder(openFolder.id).filter(matchesSearch) : [];

  /** A shared template is the master's; this page can only touch its own. */
  const selectedOwnIds = useMemo(
    () => Array.from(selectedIds).filter((id) => !(templates ?? []).find((t) => t.id === id)?.shared),
    [selectedIds, templates],
  );

  useEffect(() => {
    updateAIContext({
      filters: `Search: ${search || '(none)'}, Folder: ${openFolder?.name ?? 'All folders'}`,
      dataSummary: templates
        ? `${templates.length} template(s) in ${allFolders.length} folder(s), ${templates.filter(fromCorporate).length} from corporate`
        : 'Loading...',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, openFolder?.name, templates, allFolders.length]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['marketing', 'social-posts'] });
    qc.invalidateQueries({ queryKey: ['marketing', 'social-post-folders'] });
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const deleteMut = useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => deleteSocialPostTemplate(id)));
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      return { deleted: ids.length - failures.length, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      invalidate();
      clearSelection();
      if (deleted > 0) toast.success(`${deleted} template${deleted === 1 ? '' : 's'} deleted`);
      if (failures.length > 0) {
        toast.error(apiMessage(failures[0].reason, `${failures.length} could not be deleted`));
      }
    },
    onError: () => toast.error('Delete failed'),
  });

  const moveMut = useMutation({
    mutationFn: ({ ids, target }: { ids: number[]; target: LibraryFolder }) =>
      moveSocialPostTemplates(ids, target.id as number),
    onSuccess: ({ moved }, { target }) => {
      invalidate();
      clearSelection();
      toast.success(`${moved} template${moved === 1 ? '' : 's'} moved to ${folderLabel(target.name)}`);
    },
    onError: (e) => toast.error(apiMessage(e, 'Move failed')),
  });

  const deleteFolderMut = useMutation({
    mutationFn: (f: LibraryFolder) => deleteSocialPostFolder(f.id as number),
    onSuccess: ({ templatesDeleted }, f) => {
      invalidate();
      if (openFolder?.id === f.id) goToFolder(null);
      toast.success(
        `Folder “${folderLabel(f.name)}” deleted`,
        templatesDeleted > 0 ? `${templatesDeleted} template${templatesDeleted === 1 ? '' : 's'} deleted with it.` : undefined,
      );
    },
    onError: (e) => toast.error(apiMessage(e, 'Could not delete the folder')),
  });

  const openComposerWithTemplate = (t: SocialPostTemplate) => {
    // A copy of the content, never a link: whatever is changed in the composer
    // stays in that post, and the template is left exactly as it was.
    setSeed({ content: t.caption, media: toPickedMediaList(t.mediaItems), cover: coverOf(t) });
    setComposerOpen(true);
  };

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Drag and drop ─────────────────────────────────────────────────────────
  /**
   * Dragging a selected template carries the whole selection (own templates
   * only); dragging an unselected one carries just that one. Corporate templates
   * are not draggable — they are filed in the corporate dashboard.
   */
  const startDrag = (e: DragEvent, t: SocialPostTemplate) => {
    const ids = selectedIds.has(t.id) && selectedOwnIds.length > 0 ? selectedOwnIds : [t.id];
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(ids));
    e.dataTransfer.effectAllowed = 'move';
    setDragIds(ids);
  };

  const endDrag = () => {
    setDragIds(null);
    setDropTargetId(null);
  };

  const dropProps = (target: LibraryFolder) => ({
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetId(target.id);
    },
    onDragLeave: () => setDropTargetId((id) => (id === target.id ? null : id)),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      endDrag();
      if (!raw) return;
      const ids = (JSON.parse(raw) as number[]).filter((id) => {
        const t = (templates ?? []).find((x) => x.id === id);
        // Dropping onto the folder it is already in does nothing.
        return t && !t.shared && folderOf(t) !== target.id;
      });
      if (ids.length > 0) moveMut.mutate({ ids, target });
    },
  });

  const hasSelection = selectedIds.size > 0;
  const deletingCount = deletingFolder
    ? (templates ?? []).filter((t) => folderOf(t) === deletingFolder.id && !t.shared).length
    : 0;

  return (
    <div className={cn('flex flex-col', embedded ? '' : 'p-6')}>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <nav aria-label="Folder" className="flex items-center gap-1 text-sm min-w-0">
          {openFolder ? (
            <>
              <button
                type="button"
                onClick={() => goToFolder(null)}
                className="text-slate-500 hover:text-slate-900 hover:underline"
              >
                All folders
              </button>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden="true" />
              <span className="font-semibold text-slate-900 truncate" aria-current="page">
                {folderLabel(openFolder.name)}
              </span>
              {isCorporateFolder(openFolder) && <CorporateBadge />}
              {isSentByCorporate(openFolder) && <CorporateBadge sentFolder />}
              {canManageFolder(openFolder) && (
                <span className="flex items-center ml-1">
                  <Button size="sm" variant="ghost" title="Rename folder" onClick={() => setNameDialog({ folder: openFolder })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Delete folder" onClick={() => setDeletingFolder(openFolder)}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </span>
              )}
            </>
          ) : (
            <span className="font-semibold text-slate-900">All folders</span>
          )}
        </nav>

        <div className="flex items-center gap-2 flex-wrap flex-1 justify-end min-w-[220px]">
          <div className="flex items-center rounded-md border border-slate-200 overflow-hidden">
            {([
              ['all', 'All'],
              ['shared', 'From Corporate'],
              ['mine', 'This Account'],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => { setScopeFilter(value); clearSelection(); }}
                className={cn(
                  'px-3 py-2 text-sm transition-colors',
                  scopeFilter === value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={openFolder ? `Search ${folderLabel(openFolder.name)}...` : 'Search all folders...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {openFolder && (
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden" role="group" aria-label="View">
              {([['grid', LayoutGrid, 'Grid view'], ['list', List, 'List view']] as const).map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  title={label}
                  aria-pressed={view === mode}
                  onClick={() => setView(mode)}
                  className={cn(
                    'px-2.5 py-2 transition-colors',
                    view === mode ? 'bg-slate-100 text-slate-900' : 'bg-white text-slate-500 hover:bg-slate-50',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          )}

          <Button
            variant="secondary"
            leftIcon={<FolderPlus className="h-4 w-4" />}
            onClick={() => setNameDialog({ folder: null })}
          >
            New Folder
          </Button>
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => { setEditing(null); setEditorOpen(true); }}
          >
            New Template
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-slate-400 text-sm">Loading templates…</div>
      ) : !openFolder ? (
        /* ── Folder tiles ────────────────────────────────────────────────── */
        tiles.length === 0 ? (
          <EmptyState
            icon={<Folder className="h-10 w-10" />}
            title={q ? 'No templates match your search' : 'No folders match this filter'}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {tiles.map(({ folder: f, count, matches }) => (
              <div
                key={f.id ?? f.name}
                className="group relative rounded-lg border border-slate-200 bg-white hover:border-primary/60 hover:shadow-sm transition"
              >
                <button
                  type="button"
                  onClick={() => goToFolder(f.id)}
                  className="w-full text-left flex items-center gap-3 p-4"
                >
                  <span className="shrink-0 rounded-md bg-primary/10 text-primary p-2.5">
                    <Folder className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-900 truncate">{folderLabel(f.name)}</span>
                      {isCorporateFolder(f) && <CorporateBadge />}
                      {isSentByCorporate(f) && <CorporateBadge sentFolder />}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      {q
                        ? `${matches} match${matches === 1 ? '' : 'es'}`
                        : `${count} template${count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </button>
                {canManageFolder(f) && (
                  <div className="absolute right-2 top-2 flex opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <Button size="sm" variant="ghost" title="Rename folder" onClick={() => setNameDialog({ folder: f })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" title="Delete folder" onClick={() => setDeletingFolder(f)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {!q && (
              <button
                type="button"
                onClick={() => setNameDialog({ folder: null })}
                className="rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-primary hover:text-primary flex items-center justify-center gap-2 p-4 text-sm font-medium transition-colors"
              >
                <FolderPlus className="h-4 w-4" />
                New folder
              </button>
            )}
          </div>
        )
      ) : (
        /* ── Inside a folder ─────────────────────────────────────────────── */
        <>
          {/* Drop targets, shown only while dragging: the other folders are not
              on screen inside a folder, so they come to the drag instead. */}
          {dragIds && (
            <div className="mb-3 rounded-lg border border-dashed border-primary/50 bg-primary/5 p-2.5">
              <p className="text-xs font-medium text-slate-600 mb-2">
                Move {dragIds.length === 1 ? 'template' : `${dragIds.length} templates`} to:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {allFolders.filter((f) => f.id !== openFolder.id && f.id !== null).map((f) => (
                  <div
                    key={f.id}
                    {...dropProps(f)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm bg-white transition-colors',
                      dropTargetId === f.id ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 text-slate-700',
                    )}
                  >
                    <Folder className="h-3.5 w-3.5" />
                    {folderLabel(f.name)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasSelection && (
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-sm text-slate-500">{selectedIds.size} selected</span>
              <Button variant="ghost" size="sm" onClick={clearSelection}>Deselect all</Button>

              <div className="relative">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<FolderOpen className="h-4 w-4" />}
                  rightIcon={<ChevronDown className="h-3 w-3" />}
                  disabled={selectedOwnIds.length === 0 || moveMut.isPending}
                  title={selectedOwnIds.length === 0 ? 'Corporate templates are filed in the corporate dashboard' : undefined}
                  onClick={() => setMoveDropdownOpen((o) => !o)}
                >
                  Move
                </Button>
                {moveDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-48 rounded-md border border-slate-200 bg-white shadow-lg py-1">
                    {allFolders.filter((f) => f.id !== openFolder.id && f.id !== null).map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                        onClick={() => moveMut.mutate({ ids: selectedOwnIds, target: f })}
                      >
                        {folderLabel(f.name)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="danger"
                size="sm"
                leftIcon={<Trash2 className="h-4 w-4" />}
                disabled={selectedOwnIds.length === 0 || deleteMut.isPending}
                title={selectedOwnIds.length === 0 ? 'Corporate templates are removed in the corporate dashboard' : undefined}
                onClick={() => {
                  if (confirm(`Delete ${selectedOwnIds.length} template(s)?`)) deleteMut.mutate(selectedOwnIds);
                }}
              >
                Delete
              </Button>

              {selectedIds.size - selectedOwnIds.length > 0 && (
                <span className="text-xs text-slate-400">
                  {selectedIds.size - selectedOwnIds.length} corporate template
                  {selectedIds.size - selectedOwnIds.length === 1 ? '' : 's'} skipped
                </span>
              )}
            </div>
          )}

          {folderItems.length === 0 ? (
            <EmptyState
              icon={<MessageSquareText className="h-10 w-10" />}
              title={q ? 'No templates in this folder match your search' : 'This folder is empty'}
              description={
                q
                  ? undefined
                  : 'Prepare captions, photos, carousels and videos ahead of time. Choose accounts and timing when you use one.'
              }
            />
          ) : (
            <div
              className={
                view === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3'
                  : 'space-y-2.5'
              }
            >
              {folderItems.map((t) => {
                const itemProps = {
                  template: t,
                  selected: selectedIds.has(t.id),
                  dragging: dragIds?.includes(t.id) ?? false,
                  onToggleSelect: () => toggleSelect(t.id),
                  onUse: () => openComposerWithTemplate(t),
                  onCopy: () => { navigator.clipboard.writeText(t.caption); toast.success('Caption copied'); },
                  onEdit: () => { setEditing(t); setEditorOpen(true); },
                  onDelete: () => { if (confirm('Delete this template?')) deleteMut.mutate([t.id]); },
                  dragProps: t.shared
                    ? {}
                    : { draggable: true, onDragStart: (e: DragEvent) => startDrag(e, t), onDragEnd: endDrag },
                };
                return view === 'grid'
                  ? <TemplateCard key={t.id} {...itemProps} />
                  : <TemplateRow key={t.id} {...itemProps} />;
              })}
            </div>
          )}
        </>
      )}

      <TemplateEditor
        isOpen={editorOpen}
        template={editing}
        folders={allFolders}
        // A new template made inside a folder starts in that folder.
        defaultFolderId={openFolder?.id ?? generalId}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
        onSaved={() => { invalidate(); setEditorOpen(false); setEditing(null); }}
      />

      <FolderNameDialog
        state={nameDialog}
        onClose={() => setNameDialog(null)}
        onSaved={(created) => {
          invalidate();
          setNameDialog(null);
          if (created?.id) goToFolder(created.id);
        }}
      />

      <ConfirmDialog
        isOpen={deletingFolder !== null}
        onClose={() => setDeletingFolder(null)}
        onConfirm={() => deletingFolder ? deleteFolderMut.mutateAsync(deletingFolder).then(() => undefined) : undefined}
        title={`Delete “${deletingFolder ? folderLabel(deletingFolder.name) : ''}”?`}
        message={
          deletingCount > 0
            ? `This also deletes the ${deletingCount} template${deletingCount === 1 ? '' : 's'} inside it.`
            : 'The folder is empty.'
        }
        confirmLabel={deletingCount > 0 ? `Delete folder and ${deletingCount} template${deletingCount === 1 ? '' : 's'}` : 'Delete folder'}
      />

      <ZernioPostComposer
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        initialContent={seed.content}
        initialMedia={seed.media}
        initialCover={seed.cover}
        title="Schedule or publish template"
        allowSaveForLater={false}
        allowStartFromTemplate={false}
        onPublished={() => qc.invalidateQueries({ queryKey: ['zernio', 'posts'] })}
      />
    </div>
  );
}

function CorporateBadge({ template, sentFolder }: { template?: SocialPostTemplate; sentFolder?: boolean }) {
  // A clone is this account's own copy, so it is editable here; a shared
  // template (and a corporate folder, which passes no template) is not. A
  // folder corporate sent templates into is this account's own as well.
  const title = sentFolder
    ? 'Created by your corporate team when it sent these templates — this folder is yours to manage'
    : template && !template.shared
      ? 'Sent by your corporate team — this copy is yours to edit'
      : 'Shared by your corporate team — managed in the corporate dashboard';
  return (
    <span
      className="shrink-0 bg-primary/10 text-primary text-[10px] font-semibold px-1.5 py-0.5 rounded"
      title={title}
    >
      Corporate
    </span>
  );
}

// ─── Template items ───────────────────────────────────────────────────────────

interface ItemProps {
  template: SocialPostTemplate;
  selected: boolean;
  dragging: boolean;
  onToggleSelect: () => void;
  onUse: () => void;
  onCopy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  dragProps: {
    draggable?: boolean;
    onDragStart?: (e: DragEvent) => void;
    onDragEnd?: () => void;
  };
}

/**
 * The first item stands for the set; a badge says how many more there are.
 * Clicking it plays every item back for review.
 */
function TemplateThumb({ template: t, className }: { template: SocialPostTemplate; className?: string }) {
  const [previewing, setPreviewing] = useState(false);
  if (t.mediaItems.length === 0) return <TemplateThumbImage template={t} className={className} />;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setPreviewing(true); }}
        aria-label="Preview media"
        title="Preview media"
        className={cn('block text-left', className)}
      >
        <TemplateThumbImage template={t} className="h-full w-full" />
      </button>
      <SocialMediaPreviewModal
        isOpen={previewing}
        onClose={() => setPreviewing(false)}
        title="Template preview"
        caption={t.caption}
        items={t.mediaItems.map((m) => ({
          type: m.type,
          url: m.previewUrl ?? m.url,
          thumbnail: m.type === 'video' ? t.coverPreviewUrl ?? t.coverImageUrl : null,
        }))}
      />
    </>
  );
}

function TemplateThumbImage({ template: t, className }: { template: SocialPostTemplate; className?: string }) {
  const first = t.mediaItems[0];
  return (
    <div className={cn('overflow-hidden bg-slate-100 relative', className)}>
      {t.mediaItems.length > 1 && (
        <span className="absolute top-1 left-1 z-10 bg-black/70 text-white text-[10px] font-medium px-1 rounded">
          {t.mediaItems.length}
        </span>
      )}
      {first?.type === 'video' ? (
        <>
          {t.coverImageUrl ? (
            <img src={t.coverPreviewUrl ?? t.coverImageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <video src={first.previewUrl ?? first.url} className="w-full h-full object-cover" preload="metadata" muted playsInline tabIndex={-1} />
          )}
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="bg-black/45 rounded-full p-1.5">
              <Play className="h-3 w-3 text-white" fill="white" />
            </span>
          </span>
          {formatDuration(first.duration) && (
            <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 rounded">
              {formatDuration(first.duration)}
            </span>
          )}
        </>
      ) : first?.type === 'image' ? (
        <img src={first.previewUrl ?? first.url} alt={first.altText || ''} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <span className="w-full h-full flex items-center justify-center text-slate-300">
          <MessageSquareText className="h-6 w-6" />
        </span>
      )}
    </div>
  );
}

function mediaSummary(t: SocialPostTemplate): string | null {
  if (t.mediaItems.length === 0) return null;
  if (t.mediaItems.length === 1) {
    return `${t.mediaItems[0].type === 'video' ? 'Video' : 'Image'} ${formatBytes(t.mediaItems[0].fileSize)}`;
  }
  return `${t.mediaItems.length} media items · ${formatBytes(t.mediaItems.reduce((sum, m) => sum + m.fileSize, 0))}`;
}

function SelectBox({ selected, onToggle, className }: { selected: boolean; onToggle: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn('shrink-0', className)}
      aria-label={selected ? 'Deselect template' : 'Select template'}
    >
      <span className={cn(
        'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors bg-white',
        selected ? 'bg-primary border-primary' : 'border-slate-300 hover:border-primary',
      )}>
        {selected && <Check className="h-2.5 w-2.5 text-white" />}
      </span>
    </button>
  );
}

function ItemActions({ template: t, onCopy, onEdit, onDelete }: Pick<ItemProps, 'template' | 'onCopy' | 'onEdit' | 'onDelete'>) {
  return (
    <div className="flex gap-1">
      <Button size="sm" variant="ghost" title="Copy caption" onClick={onCopy}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
      {!t.shared && (
        <>
          <Button size="sm" variant="ghost" title="Edit template" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" title="Delete template" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5 text-red-500" />
          </Button>
        </>
      )}
    </div>
  );
}

function UseButton({ onUse, className }: { onUse: () => void; className?: string }) {
  return (
    <Button
      size="sm"
      className={className}
      leftIcon={<Send className="h-3.5 w-3.5" />}
      title="Choose accounts, then schedule or publish. The template stays as it is."
      onClick={onUse}
    >
      Use template
    </Button>
  );
}

function TemplateCard(props: ItemProps) {
  const { template: t, selected, dragging, onToggleSelect, onUse, dragProps } = props;
  const media = mediaSummary(t);
  return (
    <div
      {...dragProps}
      className={cn(
        'flex flex-col rounded-lg border bg-white overflow-hidden transition',
        selected ? 'border-primary ring-1 ring-primary/40' : 'border-slate-200 hover:border-slate-300',
        dragging && 'opacity-50',
        dragProps.draggable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="relative">
        <TemplateThumb template={t} className="aspect-square w-full" />
        <SelectBox selected={selected} onToggle={onToggleSelect} className="absolute top-2 left-2 z-20" />
        {fromCorporate(t) && <span className="absolute top-2 right-2 z-20"><CorporateBadge template={t} /></span>}
      </div>
      <div className="flex-1 flex flex-col p-3 gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700 line-clamp-3 whitespace-pre-wrap break-words">{t.caption}</p>
          <p className="text-[11px] text-slate-400 mt-1.5">
            {t.caption.length} characters{media ? ` · ${media}` : ''}
          </p>
        </div>
        {/* The icons always sit on their own line under the button, so they are
            never clipped however narrow the card gets. */}
        <div className="flex flex-col items-start gap-1">
          <UseButton onUse={onUse} />
          <ItemActions {...props} />
        </div>
      </div>
    </div>
  );
}

function TemplateRow(props: ItemProps) {
  const { template: t, selected, dragging, onToggleSelect, onUse, dragProps } = props;
  const media = mediaSummary(t);
  return (
    <div
      {...dragProps}
      className={cn(
        'flex gap-3 rounded-lg border p-3 transition-colors bg-white',
        selected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300',
        dragging && 'opacity-50',
        dragProps.draggable && 'cursor-grab active:cursor-grabbing',
      )}
    >
      <SelectBox selected={selected} onToggle={onToggleSelect} className="self-start mt-0.5" />
      <TemplateThumb template={t} className="shrink-0 w-[76px] h-[76px] rounded-md border border-slate-200" />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 line-clamp-2 whitespace-pre-wrap break-words">{t.caption}</p>
          </div>
          {fromCorporate(t) && <CorporateBadge template={t} />}
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          {t.caption.length} characters{media ? ` · ${media}` : ''}
        </p>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1.5">
        <UseButton onUse={onUse} />
        <ItemActions {...props} />
      </div>
    </div>
  );
}

// ─── Folder name dialog (create + rename) ─────────────────────────────────────

function FolderNameDialog({
  state,
  onClose,
  onSaved,
}: {
  /** null = closed; `folder: null` = creating; otherwise renaming that folder. */
  state: { folder: LibraryFolder | null } | null;
  onClose: () => void;
  onSaved: (created: LibraryFolder | null) => void;
}) {
  const toast = useToast();
  const renaming = state?.folder ?? null;
  const [name, setName] = useState('');

  useEffect(() => {
    if (state) setName(renaming?.name ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const saveMut = useMutation({
    mutationFn: () =>
      renaming
        ? renameSocialPostFolder(renaming.id as number, name.trim()).then(() => null)
        : createSocialPostFolder(name.trim()),
    onSuccess: (created) => {
      toast.success(renaming ? 'Folder renamed' : 'Folder created');
      onSaved(created);
    },
    onError: (e) => toast.error(apiMessage(e, renaming ? 'Could not rename the folder' : 'Could not create the folder')),
  });

  const unchanged = renaming !== null && name.trim() === renaming.name;

  return (
    <Modal
      isOpen={state !== null}
      onClose={onClose}
      title={renaming ? 'Rename folder' : 'New folder'}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={!name.trim() || unchanged || saveMut.isPending}>
            {renaming ? 'Rename' : 'Create folder'}
          </Button>
        </div>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); if (name.trim() && !unchanged) saveMut.mutate(); }}>
        <Input
          label="Folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
          placeholder="e.g. Summer campaign"
        />
      </form>
    </Modal>
  );
}

// ─── Template editor ──────────────────────────────────────────────────────────

/**
 * Create or edit one of THIS ACCOUNT's templates.
 *
 * The same caption box, media picker and cover picker as the New Post composer —
 * the components themselves, not copies — so a template holds exactly what a
 * post can: several photos as a carousel, a video or Reel, its cover, in order.
 *
 * WHAT IS MISSING ON PURPOSE: accounts, platform, date. A template has no
 * destination until it is used, so the media picker runs with no platforms and
 * applies only the rules every post shares (formats, sizes, up to 10 items, one
 * video, no mixing). Each platform's own limits — a Reel's length, a Story's
 * shape, how many photos a network takes — are checked in the composer once
 * accounts are chosen, where the message can name the platform at fault.
 *
 * The folder is chosen by id, not name: a corporate folder and one of this
 * account's can share a name.
 */
function TemplateEditor({
  isOpen,
  template,
  folders,
  defaultFolderId,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  template: SocialPostTemplate | null;
  folders: LibraryFolder[];
  /** Where a NEW template starts: the folder the user is in, else General. */
  defaultFolderId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [caption, setCaption] = useState('');
  const [folderId, setFolderId] = useState<number | null>(null);
  const [media, setMedia] = useState<PickedMedia[]>([]);
  const [cover, setCover] = useState<CoverSelection | null>(null);
  const [uploading, setUploading] = useState(false);

  // Seed on open, so reopening on a different template does not keep stale content.
  useEffect(() => {
    if (!isOpen) return;
    setCaption(template?.caption ?? '');
    setFolderId(template ? (template.folderID > 0 ? template.folderID : defaultFolderId) : defaultFolderId);
    setMedia(toPickedMediaList(template?.mediaItems ?? []));
    setCover(template ? coverOf(template) : null);
    setUploading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template]);

  const hasVideo = media.some((m) => m.type === 'video');

  const saveMut = useMutation({
    mutationFn: () => {
      // A cover only means something beside a video. Removing the video after
      // choosing one would otherwise leave a stale cover on the template.
      const keptCover = hasVideo ? cover : null;
      const input = {
        caption: caption.trim(),
        ...(folderId ? { folderId } : { folder: 'general' }),
        mediaItems: toTemplateMediaList(media),
        coverImageUrl: keptCover?.imageUrl ?? null,
        coverOffsetMs: keptCover?.imageUrl ? null : keptCover?.offsetMs ?? null,
      };
      return template
        ? updateSocialPostTemplate(template.id, input)
        : createSocialPostTemplate(input);
    },
    onSuccess: () => {
      toast.success(template ? 'Template updated' : 'Template saved');
      onSaved();
    },
    onError: (e: unknown) => toast.error(apiMessage(e, 'Save failed')),
  });

  const busy = saveMut.isPending || uploading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={template ? 'Edit template' : 'New template'}
      size="lg"
      closeOnEscape={false}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={!caption.trim() || busy}>
            {template ? 'Save changes' : 'Save as template'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Caption"
          required
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="What do you want to say?"
          rows={5}
          hint={`${caption.length} characters`}
          autoFocus
        />

        <ZernioMediaListPicker
          value={media}
          onChange={setMedia}
          platforms={[]}
          noun="template"
          onUploadingChange={setUploading}
          disabled={saveMut.isPending}
        />

        <ZernioCoverPicker
          value={cover}
          onChange={setCover}
          hasVideo={hasVideo}
          videoUrl={firstVideoSrc(media)}
          itemCount={media.length}
          onUploadingChange={setUploading}
          disabled={saveMut.isPending}
        />

        <div>
          <label className="text-sm font-medium text-slate-700 block mb-1">Folder</label>
          <select
            value={folderId ?? ''}
            onChange={(e) => setFolderId(Number(e.target.value) || null)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            {folders.filter((f) => f.id !== null).map((f) => (
              <option key={f.id} value={f.id as number}>
                {folderLabel(f.name)}{isCorporateFolder(f) ? ' (Corporate)' : ''}
              </option>
            ))}
          </select>
        </div>

        <p className="text-xs text-slate-500">
          Nothing is posted or scheduled. Choose accounts and timing when you use this template, and it
          will be checked against each platform&apos;s rules then.
        </p>
      </div>
    </Modal>
  );
}
